import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { cn } from '../lib/cn.js';


export interface EntryColumn {
  key: string;
  label: ReactNode;
  width?: string;
  align?: 'left' | 'right' | 'center';
  numeric?: boolean;
  required?: boolean;
  noPrint?: boolean;
}

const ALIGN = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const;

export interface EntryGridProps {
  columns: EntryColumn[];
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
  'aria-label': string;
}

export function EntryGrid({
  columns,
  children,
  footer,
  className,
  'aria-label': ariaLabel,
}: EntryGridProps) {
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full table-fixed border-collapse" aria-label={ariaLabel}>
        <colgroup>
          {columns.map((c) => (
            <col key={c.key} style={c.width ? { width: c.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr className="bg-[var(--surface-2)]">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                data-noprint={c.noPrint ? '1' : undefined}
                className={cn(
                  'border-b border-[var(--line-2)] px-2.5 py-2',
                  'text-[10px] font-medium tracking-[0.08em] whitespace-nowrap text-[var(--ink-3)] uppercase',
                  ALIGN[c.align ?? (c.numeric ? 'right' : 'left')],
                )}
              >
                {c.label}
                {c.required && (
                  <span className="pl-0.5 text-[var(--debit)]" aria-hidden="true">
                    *
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
        {footer && <tfoot className="bg-[var(--surface-2)] font-medium">{footer}</tfoot>}
      </table>
    </div>
  );
}

export interface EntryRowProps {
  children: ReactNode;
  tone?: 'default' | 'invalid' | 'generated';
  className?: string;
}

const ROW_TONE = {
  default: '',
  invalid: 'bg-[var(--debit-soft)]/45',
  generated: 'bg-[var(--surface-2)]',
} as const;

export function EntryRow({ children, tone = 'default', className }: EntryRowProps) {
  return (
    <tr
      className={cn(
        'bb-row-h border-b border-[var(--line-2)]',
        ROW_TONE[tone],
        tone === 'default' && 'focus-within:bg-[var(--accent-soft)]/25',
        className,
      )}
    >
      {children}
    </tr>
  );
}

export interface EntryCellProps {
  children?: ReactNode;
  align?: 'left' | 'right' | 'center';
  numeric?: boolean;
  noPrint?: boolean;
  padded?: boolean;
  className?: string;
}

export function EntryCell({
  children,
  align,
  numeric,
  noPrint,
  padded,
  className,
}: EntryCellProps) {
  return (
    <td
      data-noprint={noPrint ? '1' : undefined}
      className={cn(
        'p-0 align-middle text-[12.5px] text-[var(--ink)]',
        padded && 'px-2.5',
        ALIGN[align ?? (numeric ? 'right' : 'left')],
        numeric && 'bb-num tabular-nums',
        className,
      )}
    >
      {children}
    </td>
  );
}

const CELL_CONTROL = cn(
  'h-[calc(var(--row)-1px)] w-full border-0 bg-transparent px-2.5 text-[12.5px]',
  'text-[var(--ink)] outline-none placeholder:text-[var(--ink-3)]',
  'focus:bg-[var(--accent-soft)]/60 focus:shadow-[inset_0_0_0_2px_var(--accent)]',
  'disabled:cursor-not-allowed disabled:text-[var(--ink-3)]',
  'aria-[invalid=true]:bg-[var(--debit-soft)] aria-[invalid=true]:text-[var(--debit)]',
);

export { CELL_CONTROL as GRID_CELL_CONTROL };

export interface GridInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  numeric?: boolean;
}

export const GridInput = forwardRef<HTMLInputElement, GridInputProps>(function GridInput(
  { invalid, numeric, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      inputMode={numeric ? 'decimal' : undefined}
      className={cn(CELL_CONTROL, numeric && 'bb-num text-right tabular-nums', className)}
      {...rest}
    />
  );
});

export interface GridSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  invalid?: boolean;
}

export const GridSelect = forwardRef<HTMLSelectElement, GridSelectProps>(function GridSelect(
  { invalid, className, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(CELL_CONTROL, 'cursor-pointer appearance-none pr-6', className)}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%238b9aa0' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 7px center',
      }}
      {...rest}
    >
      {children}
    </select>
  );
});

export function GridAction({
  label,
  onClick,
  disabled,
  children,
  className,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'grid h-[calc(var(--row)-1px)] w-full cursor-pointer place-items-center border-0 bg-transparent',
        'text-[var(--ink-3)] hover:text-[var(--debit)]',
        'focus-visible:shadow-[inset_0_0_0_2px_var(--accent)] focus-visible:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-[var(--ink-3)]',
        className,
      )}
    >
      {children}
    </button>
  );
}
